// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ProjectModel } from '../model/project.model';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ProjectUtils } from '../components/project/util/project-utils';
import { StringUtils } from '@shared/util/string-utils';

@Injectable({providedIn: 'root'})
export class ProjectArticleService {

    constructor(
        public projectModel: ProjectModel,
        private containerService: ContainerService,
        private articleManager: ArticleManager
    ) {}

    public get currentProject () {
        return this.projectModel.currentProject;
    }

    public createNewArticleUsage(articleId: number, version: number, groupId: string = null): ArticleUsageVO {
      return this.internalCreateNewArticleUsage(articleId, version, groupId, true);
    }

    private internalCreateNewArticleUsage(
      articleId: number,
      version: number,
      groupId: string = null,
      isStoreArticleUsage: boolean = true
    ): ArticleUsageVO {
      const usage: ArticleUsageVO = new ArticleUsageVO();
      usage.articleId = articleId;
      usage.articleVersion = version;
      usage.id = StringUtils.createUuid();
      usage.groupId = groupId;
      usage.metaElementUsages = [];
      usage.articleVisible = true;
      if (isStoreArticleUsage) {
        this.storeArticleUsage(usage);
      }
      return usage;
    }

    public storeArticleUsage(articleUsage: ArticleUsageVO): void {
      this.projectModel.articleUsageCache.setValue(articleUsage.id, articleUsage);
    }

    public createUnstoredNewArticleUsage(articleId: number, version: number, groupId: string = null): ArticleUsageVO {
      return this.internalCreateNewArticleUsage(articleId, version, groupId, false);
    }

    public findArticleUsageIdsByGroupIds(groupIds: string[]): string[] {
      if (!groupIds || groupIds.length === 0) {
        return [];
      }
      let usageIds: string[] = [];
      for (const groupId of groupIds) {
        usageIds = usageIds.concat(this.findArticleUsageIdsByGroupId(groupId));
      }
      return usageIds;
    }

    public collectGroupArticleUsageIds(articleUsage: ArticleUsageVO): string[] {
      let usageIds: string[] = [];
      if (!articleUsage) {
        return usageIds;
      }

      if (!articleUsage.groupId) {
        usageIds.push(articleUsage.id);
      } else {
        usageIds = this.findArticleUsageIdsByGroupId(articleUsage.groupId);
      }
      return usageIds;
    }

    public findArticleUsageIdsByGroupId(groupId: string): string[] {
      const usageIds: string[] = [];
      if (!groupId) {
        return usageIds;
      }
      this.projectModel.articleUsageCache.forEach((_usageId: string, articleUsage: ArticleUsageVO) => {
        if (articleUsage.groupId && articleUsage.groupId === groupId) {
          usageIds.push(articleUsage.id);
        }
      });
      return usageIds;
    }

    public findArticleUsagesByType(articleType: string): ArticleUsageVO[] {
      return this.findArticleUsagesByTypes([articleType]);
    }

    public findArticleUsagesByTypes(articleTypes: string[]): ArticleUsageVO[] {
      const result: ArticleUsageVO[] = [];
      for (const usage of this.projectModel.articleUsageCache.values()) {
        const article: ArticleVO = this.articleManager.getCachedArticle(usage.articleId, usage.articleVersion);
        if (article && articleTypes.indexOf(article.type.displayName) >= 0) {
          result.push(usage);
        }
      }
      return result;
    }

  public findLastAddedArticleUsages(lastNArticles: number): ArticleUsageVO[] {
    return ProjectUtils.articleUsages(this.currentProject).slice(-lastNArticles).reverse();
  }

    public getConnectedArticlesWithRelative(parentContainer: ArticleContainer, searchRecursively: boolean = true): ArticleContainer[] {
      const childConnectedArticles: ArticleContainer[] = this.containerService.getConnectedArticleContainers(parentContainer.articleUsage);
      const childRelativeArticles: ArticleContainer[] = this.getRelativeChildArticles(parentContainer);
      const result: ArticleContainer[] = childConnectedArticles.concat(childRelativeArticles);
      if (searchRecursively) {
        // recursive search is only done by connected child articles
        for (const child of childConnectedArticles) {
          ArrayUtils.mergeArrays(result, this.getConnectedArticlesWithRelative(child));
        }
      }
      return result;
    }

    private getRelativeChildArticles(parentContainer: ArticleContainer): ArticleContainer[] {
      const relativeChildIds: string[] = this.collectRelativeArticleUsageIdsByParentUsage(parentContainer.articleUsage);
      return this.containerService.findArticleContainersInGlobalByGuids(relativeChildIds);
    }

    /**
     * @return usage ids with "relativeParentUsageId" info property as id of the given parentUsage
     */
    public collectRelativeArticleUsageIdsByParentUsage(parentUsage: ArticleUsageVO): string[] {
      if (!parentUsage) {
        return [];
      }
      return this.findArticleUsageIdsByRelativeParentUsageId(parentUsage.id);
    }

    public getUsageIdsFromGroupWithoutChildren(articleId: string): string[] {
      let result: string[] = [];
      const parentUsage: ArticleUsageVO = this.projectModel.articleUsageCache.getValue(articleId) as ArticleUsageVO;
      if (parentUsage) {
        const articleUsageIds: string[] = this.findArticleUsageIdsByGroupId(parentUsage.groupId);
        for (const usageId of articleUsageIds) {
          if (this.findArticleUsageIdsByRelativeParentUsageId(usageId).length === 0) {
            result.push(usageId);
          }
        }
        if (result.length !== articleUsageIds.length) {
          result = [];
        }
      }
      return result;
    }

    public findArticleUsageIdsByRelativeParentUsageId(relativeParentUsageId: string): string[] {
      if (!relativeParentUsageId) {
        return [];
      }
      const usageIds: string[] = [];
      for (const usage of this.projectModel.articleUsageCache.values()) {
        const relativeUsageId: string = ArticleUsageUtils.getRelativeParentUsageId(usage);
        if (relativeUsageId === relativeParentUsageId) {
          usageIds.push(usage.id);
        }
      }
      return usageIds;
    }
}
